import { useState } from "react";
import { ArrowLeft, CalendarPlus, CheckCircle2, ClipboardList, Inbox, Stethoscope } from "lucide-react";
import BookFutureNeedDialog from "@/components/checkout/BookFutureNeedDialog";

const tutorialBookingData = {
  doctors: [{ id: "tutorial-doctor", display_name: "Dr. Reyes" }],
  chairs: [{ id: "tutorial-chair", name: "Chair 1" }],
  settings: {
    hours: {
      sunday: { closed: true },
      monday: { open: "09:00", close: "18:00", closed: false },
      tuesday: { open: "09:00", close: "18:00", closed: false },
      wednesday: { open: "09:00", close: "18:00", closed: false },
      thursday: { open: "09:00", close: "18:00", closed: false },
      friday: { open: "09:00", close: "18:00", closed: false },
      saturday: { open: "09:00", close: "15:00", closed: false },
    },
  },
};

const tutorialRctBundle = {
  procedure_name: "Root Canal Treatment",
  tooth_number: "46",
  estimatedMinutes: 60,
  category: "Endodontic",
  needs: [{ id: "tutorial-future-need-rct-46", tooth_number: "46" }],
};

const noTutorialAppointments = [];

export default function CheckoutFutureNeedTutorialDemo() {
  const [bookOpen, setBookOpen] = useState(false);
  const [booked, setBooked] = useState(false);
  const [recalled, setRecalled] = useState(false);
  const [declinedPopup, setDeclinedPopup] = useState(false);
  const [declined, setDeclined] = useState(false);

  return (
    <div className="mx-auto max-w-5xl space-y-4" data-testid="checkout-future-need-demo-page">
      <div className="flex items-center gap-3 text-sm text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          <ArrowLeft className="h-4 w-4" />
          From Doctor handoff
        </span>
        <span className="ml-auto rounded-full border border-teal-200 bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-800">
          Safe checkout tutorial
        </span>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
        <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-center">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Patient checkout</p>
            <h1 className="mt-1 text-xl font-semibold text-slate-950">Juan Dela Cruz</h1>
            <p className="mt-1 text-sm text-slate-500">Walk-in visit, chairside diagnosis completed by Dr. Reyes.</p>
          </div>
          <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-950">
            <p className="font-semibold">Future Need attached</p>
            <p className="text-blue-800">Root Canal Treatment, Tooth 46, estimated ₱8,500</p>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white px-5 py-3 shadow-sm">
        <div className="grid grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] items-center gap-2 text-sm">
          <CheckoutStep label="Today" done />
          <StepDivider />
          <CheckoutStep label="Payment" done />
          <StepDivider />
          <CheckoutStep label="Follow-up" active />
          <StepDivider />
          <CheckoutStep label="Done" />
        </div>
      </section>

      <section
        className="rounded-2xl border border-slate-200 bg-white shadow-sm"
        data-testid="checkout-future-needs-step3"
        data-tutorial-id="checkout-future-needs-step3"
      >
        <header className="border-b border-slate-100 px-5 py-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-teal-50 text-teal-700">
              <ClipboardList className="h-5 w-5" />
            </span>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Checkout Step 3</p>
              <h2 className="text-base font-semibold text-slate-950">Future Needs follow-up</h2>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-600">
                Every open Future Need must become Book, Recall, or Decline before the patient leaves the front desk.
              </p>
            </div>
          </div>
        </header>

        <div className="grid gap-4 p-5 lg:grid-cols-[minmax(0,1fr)_320px]">
          <article
            className="rounded-xl border border-blue-100 bg-blue-50/55 p-4"
            data-testid="checkout-future-need-rct-46"
            data-tutorial-id="checkout-future-need-rct-46"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-base font-semibold text-slate-950">Root Canal Treatment</p>
                <p className="mt-1 text-sm text-slate-600">RCT Tooth 46, estimated ₱8,500</p>
                <p className="mt-2 inline-flex rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-blue-800">
                  Diagnosed, not performed today
                </p>
              </div>
              {booked ? (
                <span className="rounded-full bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700">
                  Booked: Tue, May 19
                </span>
              ) : declined ? (
                <span className="rounded-full bg-rose-50 px-2.5 py-1.5 text-xs font-semibold text-rose-700">
                  Declined
                </span>
              ) : null}
            </div>
            <p className="mt-4 text-sm leading-6 text-slate-700">
              The doctor did the clinical work once: diagnosis plus procedure. Checkout now owns the operational outcome.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setBookOpen(true)}
                data-testid="checkout-future-book-rct-46"
                data-tutorial-id="checkout-future-book-rct-46"
                className="inline-flex min-h-10 items-center gap-1.5 rounded-lg bg-teal-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-teal-700"
              >
                <CalendarPlus className="h-4 w-4" />
                Book
              </button>
              <button
                type="button"
                onClick={() => setRecalled(true)}
                data-testid="checkout-future-recall-rct-46"
                data-tutorial-id="checkout-future-recall-rct-46"
                className="inline-flex min-h-10 items-center rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2 text-sm font-semibold text-amber-800 hover:bg-amber-100"
              >
                Recall
              </button>
              <button
                type="button"
                onClick={() => {
                  setDeclinedPopup(true);
                  setDeclined(true);
                }}
                data-testid="checkout-future-decline-rct-46"
                data-tutorial-id="checkout-future-decline-rct-46"
                className="inline-flex min-h-10 items-center rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100"
              >
                Decline
              </button>
            </div>
          </article>

          <aside
            className="rounded-xl border border-slate-200 bg-slate-50 p-4"
            data-testid="checkout-recall-queue"
            data-tutorial-id="checkout-recall-queue"
          >
            <div className="flex items-center gap-2">
              <Inbox className="h-4 w-4 text-slate-400" />
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Recall queue</p>
            </div>
            {recalled ? (
              <div className="mt-3 rounded-lg border border-amber-200 bg-white px-3 py-3 text-sm text-slate-800">
                <p className="font-semibold">RCT Tooth 46</p>
                <p className="mt-1 text-xs text-slate-500">Call in 14 days, estimated ₱8,500, diagnosed by Dr. Reyes.</p>
              </div>
            ) : (
              <p className="mt-3 text-sm leading-6 text-slate-500">
                Nothing is staged yet. Recall appears here only after staff chooses that outcome.
              </p>
            )}
          </aside>
        </div>
      </section>

      <BookFutureNeedDialog
        open={bookOpen}
        onOpenChange={setBookOpen}
        bundle={tutorialRctBundle}
        patientId="tutorial-patient"
        patientName="Juan Dela Cruz"
        defaultDoctorId="tutorial-doctor"
        defaultChairId="tutorial-chair"
        headingOverride="Book Root Canal Treatment"
        submitLabel="Book this slot"
        demoMode
        demoInitialDate="2026-05-19"
        demoReferenceData={tutorialBookingData}
        demoDayAppointments={noTutorialAppointments}
        demoUpcomingAppointments={noTutorialAppointments}
        onBooked={() => setBooked(true)}
      />

      {declinedPopup ? (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-950/45 p-4">
          <section
            className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl"
            data-testid="checkout-future-declined-popup"
            data-tutorial-id="checkout-future-declined-popup"
          >
            <div className="flex items-center gap-2 text-rose-700">
              <Stethoscope className="h-4 w-4" />
              <h3 className="text-base font-semibold">Declined</h3>
            </div>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              This RCT need is declined and is not actionable from here on forth.
            </p>
            <button
              type="button"
              onClick={() => setDeclinedPopup(false)}
              className="mt-4 rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white"
            >
              Close
            </button>
          </section>
        </div>
      ) : null}
    </div>
  );
}

function CheckoutStep({ label, active = false, done = false }) {
  return (
    <div className={["flex items-center gap-2", active ? "text-teal-700" : done ? "text-emerald-700" : "text-slate-400"].join(" ")}>
      <span className={[
        "inline-flex h-7 w-7 items-center justify-center rounded-full border text-xs font-bold",
        active ? "border-teal-300 bg-teal-50" : done ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50",
      ].join(" ")}>
        {done ? <CheckCircle2 className="h-4 w-4" /> : null}
      </span>
      <span className="font-semibold">{label}</span>
    </div>
  );
}

function StepDivider() {
  return <span className="h-px w-8 bg-slate-200" aria-hidden="true" />;
}
